/**
 * /prompts/:id — detail page (Story 7.6 AC2.b/c/d).
 *
 * Layout:
 *   - Header: prompt name + description.
 *   - Actions: "Nova versão" (operator+) launches NewVersionDialog.
 *   - Versions table: newest first; active version is badged. Each row offers
 *     "Ver diff" (stub until Story 8.2), "Ativar" (non-active newer versions)
 *     and "Rollback" (non-active older versions).
 *
 * Rollback vs. Activate is a UX nuance — both call the same endpoint
 * (POST …/:versionId/activate). See ActivateConfirmDialog for the wording.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Eye, GitCompare, Plus, Undo2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { ApiError, apiClient } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';
import { ActivateConfirmDialog } from '@/components/prompts/ActivateConfirmDialog';
import { NewVersionDialog } from '@/components/prompts/NewVersionDialog';
import { PromptDiffViewer } from '@/components/prompts/PromptDiffViewer';
import { ViewVersionDialog } from '@/components/prompts/ViewVersionDialog';
import type { PromptEditorValue } from '@/components/prompts/PromptEditor';
import type {
  PromptDetailResponse,
  PromptVersion,
  PromptVersionSummary,
} from '@/lib/prompts-types';

function canMutate(role: string | undefined): boolean {
  return role === 'operator' || role === 'admin';
}

/**
 * Story 8.6 AC7: rollback is admin-only. Operators can still move forward
 * (activate a newer version) but cannot roll back to an older one.
 */
function canRollback(role: string | undefined): boolean {
  return role === 'admin';
}

export function PromptDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const qc = useQueryClient();

  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [newVersionSeed, setNewVersionSeed] = useState<PromptEditorValue | undefined>(undefined);
  const [activationTarget, setActivationTarget] = useState<PromptVersionSummary | null>(null);
  const [diffTarget, setDiffTarget] = useState<PromptVersionSummary | null>(null);
  const [viewTarget, setViewTarget] = useState<PromptVersionSummary | null>(null);

  const queryKey = useMemo(() => ['admin', 'prompts', id] as const, [id]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => apiClient.get<PromptDetailResponse>(`/admin/prompts/${id}`),
    enabled: Boolean(id),
  });

  const activateMutation = useMutation({
    mutationFn: (versionId: string) =>
      apiClient.post<PromptVersion>(`/admin/prompts/${id}/versions/${versionId}/activate`),
    onSuccess: (version) => {
      toast.success(`v${version.versionNumber} ativada.`);
      setActivationTarget(null);
      void qc.invalidateQueries({ queryKey });
      void qc.invalidateQueries({ queryKey: ['admin', 'prompts'] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 404) {
        toast.error('Versão não encontrada.');
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        toast.error('Apenas administradores podem reverter para uma versão anterior.');
        return;
      }
      toast.error('Falha ao ativar versão.');
    },
  });

  const activeVersionNumber = useMemo(() => {
    const active = data?.versions.find((v) => v.isActive);
    return active?.versionNumber ?? null;
  }, [data]);

  const activeVersion = useMemo(() => data?.versions.find((v) => v.isActive) ?? null, [data]);

  // Lazy-fetch the FULL active version (systemPrompt + few-shots + schema) so
  // "Nova versão" can pre-populate from it without making operators hunt for
  // the fork button. Cached by react-query — only fires once per active id.
  const activeVersionFullQuery = useQuery({
    queryKey: ['admin', 'prompts', id, 'versions', activeVersion?.id, 'full'],
    queryFn: () =>
      apiClient.get<PromptVersion>(`/admin/prompts/${id}/versions/${activeVersion!.id}`),
    enabled: Boolean(id && activeVersion?.id),
  });

  const activeSeed: PromptEditorValue | undefined = useMemo(() => {
    const v = activeVersionFullQuery.data;
    // Defensive: only seed when the full payload actually arrived. If the
    // endpoint returned a partial / wrong-shape blob (e.g. test mocks that
    // alias the detail response), fall back to undefined → blank form.
    if (!v || typeof v.systemPrompt !== 'string') return undefined;
    return {
      systemPrompt: v.systemPrompt,
      fewShotsText: JSON.stringify(v.fewShotExamples ?? [], null, 2),
      jsonSchemaText: JSON.stringify(v.jsonOutputSchema ?? {}, null, 2),
      changeSummary: `Forkado de v${v.versionNumber}`,
    };
  }, [activeVersionFullQuery.data]);

  if (!id) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">ID de prompt inválido.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/prompts"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
        >
          <ArrowLeft className="size-3" /> Voltar para prompts
        </Link>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {isError && (
        <div className="space-y-2">
          <p className="text-sm text-destructive">Prompt não encontrado ou falha de rede.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </div>
      )}

      {data && (
        <>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">{data.prompt.name}</h1>
              <p className="text-sm text-muted-foreground">
                {data.prompt.description ?? 'Sem descrição.'}
              </p>
            </div>
            {canMutate(user?.role) && (
              <div className="flex gap-2">
                {activeVersion && (
                  <Button variant="outline" onClick={() => setViewTarget(activeVersion)}>
                    <Eye /> Ver ativa
                  </Button>
                )}
                <Button
                  onClick={() => {
                    // Default: fork from active version so the operator edits
                    // diffs instead of starting blank. Falls back to undefined
                    // (blank form) when no active version exists yet.
                    setNewVersionSeed(activeSeed);
                    setNewVersionOpen(true);
                  }}
                  disabled={Boolean(activeVersion) && activeVersionFullQuery.isLoading}
                >
                  <Plus /> Nova versão
                </Button>
              </div>
            )}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Versões</CardTitle>
            </CardHeader>
            <CardContent>
              {data.versions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhuma versão criada ainda. Crie a primeira para ativar este prompt.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-2 pr-4 font-medium">Versão</th>
                        <th className="py-2 pr-4 font-medium">Status</th>
                        <th className="py-2 pr-4 font-medium">Resumo</th>
                        <th className="py-2 pr-4 font-medium">Criada em</th>
                        <th className="py-2 pr-4 font-medium">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.versions.map((v) => {
                        const isOlderThanActive =
                          activeVersionNumber !== null && v.versionNumber < activeVersionNumber;
                        return (
                          <tr key={v.id} className="border-b last:border-b-0 align-top">
                            <td className="py-2 pr-4 font-mono">v{v.versionNumber}</td>
                            <td className="py-2 pr-4">
                              {v.isActive ? (
                                <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                                  <CheckCircle2 className="size-3" /> ativa
                                </span>
                              ) : (
                                <span className="text-xs italic text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="py-2 pr-4 text-xs text-muted-foreground">
                              {v.changeSummary ?? '—'}
                            </td>
                            <td className="py-2 pr-4 text-xs text-muted-foreground">
                              {formatDateTime(v.createdAt)}
                            </td>
                            <td className="py-2 pr-4">
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  title="Ver conteúdo desta versão"
                                  onClick={() => setViewTarget(v)}
                                >
                                  <Eye className="size-3" /> Ver
                                </Button>
                                {data.prompt.activeVersionId && !v.isActive && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    title="Diff vs versão ativa"
                                    onClick={() => setDiffTarget(v)}
                                  >
                                    <GitCompare className="size-3" /> Ver diff
                                  </Button>
                                )}
                                {!v.isActive &&
                                  canMutate(user?.role) &&
                                  (isOlderThanActive ? (
                                    canRollback(user?.role) ? (
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => setActivationTarget(v)}
                                      >
                                        <Undo2 className="size-3" /> Rollback
                                      </Button>
                                    ) : null
                                  ) : (
                                    <Button
                                      variant="default"
                                      size="sm"
                                      onClick={() => setActivationTarget(v)}
                                    >
                                      Ativar
                                    </Button>
                                  ))}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {canMutate(user?.role) && data && (
        <NewVersionDialog
          promptId={id}
          open={newVersionOpen}
          onOpenChange={(next) => {
            setNewVersionOpen(next);
            if (!next) setNewVersionSeed(undefined);
          }}
          initialValue={newVersionSeed}
          onCreated={() => {
            void qc.invalidateQueries({ queryKey });
          }}
        />
      )}

      {viewTarget && (
        <ViewVersionDialog
          promptId={id}
          versionId={viewTarget.id}
          versionNumber={viewTarget.versionNumber}
          isActive={viewTarget.isActive}
          open
          onOpenChange={(next) => {
            if (!next) setViewTarget(null);
          }}
          onForkAsNewVersion={
            canMutate(user?.role)
              ? (seed) => {
                  setNewVersionSeed({
                    ...seed,
                    changeSummary: `Forkado de v${viewTarget.versionNumber}`,
                  });
                  setViewTarget(null);
                  setNewVersionOpen(true);
                }
              : undefined
          }
        />
      )}

      {activationTarget && activeVersionNumber !== null && (
        <ActivateConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setActivationTarget(null);
          }}
          mode={activationTarget.versionNumber < activeVersionNumber ? 'rollback' : 'activate'}
          versionNumber={activationTarget.versionNumber}
          previousActiveVersionNumber={activeVersionNumber}
          onConfirm={() => activateMutation.mutate(activationTarget.id)}
          pending={activateMutation.isPending}
        />
      )}
      {activationTarget && activeVersionNumber === null && (
        <ActivateConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setActivationTarget(null);
          }}
          mode="activate"
          versionNumber={activationTarget.versionNumber}
          previousActiveVersionNumber={null}
          onConfirm={() => activateMutation.mutate(activationTarget.id)}
          pending={activateMutation.isPending}
        />
      )}

      {diffTarget && data?.prompt.activeVersionId && (
        <PromptDiffViewer
          open
          onOpenChange={(next) => {
            if (!next) setDiffTarget(null);
          }}
          promptId={id}
          versionAId={data.prompt.activeVersionId}
          versionBId={diffTarget.id}
          versionANumber={activeVersionNumber ?? undefined}
          versionBNumber={diffTarget.versionNumber}
        />
      )}
    </div>
  );
}

export default PromptDetailPage;
